import React, { useMemo, useState } from 'react';
import { X, MapPin, Upload } from 'lucide-react';
import { ServiceExecutiveService } from '../../services/serviceExecutiveService';

const SOURCE_OPTIONS = [
  { label: 'Select Source', value: '' },
  { label: 'Google Ads', value: 'google_ads' },
  { label: 'Roadserve Membership', value: 'roadserve_membership' },
  { label: 'Toll Free', value: 'toll_free' },
  { label: 'Other', value: 'other' },
];

const SERVICE_TYPE_OPTIONS = [
  { label: 'Select Service Type', value: '' },
  { label: 'Emergency Roadside Assistance', value: 'emergency_roadside' },
  { label: 'Towing Service', value: 'towing' },
  { label: 'Jump Start', value: 'jump_start' },
  { label: 'Flat Tyre Change', value: 'flat_tyre' },
  { label: 'Fuel Delivery', value: 'fuel_delivery' },
  { label: 'Lockout Service', value: 'lockout' },
  { label: 'Other', value: 'other' },
];

const cleanPhoneNumber = (value) => value.replace(/\D/g, '').slice(-10);

const ServiceExecutiveComplaintRegistrationModal = ({ open, onClose, executiveId, executiveName, onSuccess }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    phoneNumber: '',
    vehicleDetails: '',
    source: '',
    customSource: '',
    locationLink: '',
    quotedAmount: '',
    advancePayment: '',
    mediaFiles: [],
    problemDescription: '',
    alternateNumber: '',
    serviceType: '',
    dropLocation: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const isOtherSource = formData.source === 'other';

  const payloadSource = useMemo(() => {
    if (isOtherSource && formData.customSource.trim()) {
      return formData.customSource.trim();
    }
    return formData.source || 'web';
  }, [formData.customSource, formData.source, isOtherSource]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.customerName.trim()) return alert('Customer name is required');
    if (!formData.phoneNumber.trim()) return alert('Phone number is required');
    if (!formData.vehicleDetails.trim()) return alert('Vehicle details are required');
    if (!formData.problemDescription.trim()) return alert('Problem description is required');
    if (!formData.serviceType.trim()) return alert('Service type is required');
    if (formData.serviceType === 'towing' && !formData.dropLocation.trim()) {
      return alert('Drop location is required for towing service');
    }

    setSubmitting(true);

    let uploadedMediaUrls = [];
    if (formData.mediaFiles.length > 0) {
      const uploadResult = await ServiceExecutiveService.uploadComplaintMedia(
        formData.mediaFiles,
        executiveId
      );
      if (uploadResult.success) {
        uploadedMediaUrls = uploadResult.urls;
      } else {
        alert(uploadResult.error || 'Some media files failed to upload. Continuing without them.');
      }
    }

    const requestData = {
      customer_name: formData.customerName,
      vehicle_number: formData.vehicleDetails,
      service_type: formData.serviceType,
      priority: 'medium',
      contact_number: formData.phoneNumber,
      alternate_number: formData.alternateNumber,
      problem: formData.problemDescription,
      address: '',
      pincode: '',
      customer_quoted_amount: parseFloat(formData.quotedAmount) || 0,
      advance_payment: formData.advancePayment,
      service_tag: '',
      location_link: formData.locationLink,
      media_upload: uploadedMediaUrls,
      source: payloadSource,
      remark: '',
      is_premium: false,
      card_number: '',
      drop_location: formData.dropLocation || undefined,
    };

    const result = await ServiceExecutiveService.createServiceRequest(requestData, executiveId, executiveName);
    if (result.success) {
      try {
        const whatsappResult = await ServiceExecutiveService.sendWhatsAppLocationRequest(formData.phoneNumber);
        if (whatsappResult.success && result.requestId) {
          await ServiceExecutiveService.updateWhatsAppLocationSent(result.requestId);
        }
      } catch (error) {
        console.error('WhatsApp location request failed:', error);
      }

      alert(result.message || 'Service request created');
      onSuccess?.();
      onClose();
    } else {
      alert(result.message || 'Failed to create request');
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Complaint Registration</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
            <input
              value={formData.customerName}
              onChange={(event) => handleChange('customerName', event.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2"
              placeholder="Enter customer name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <div className="flex gap-2">
              <input
                value={formData.phoneNumber}
                onChange={(event) => handleChange('phoneNumber', cleanPhoneNumber(event.target.value))}
                className="w-full border border-gray-200 rounded-lg p-2"
                placeholder="Enter phone number"
              />
              {formData.phoneNumber.length === 10 && (
                <button
                  type="button"
                  onClick={() => ServiceExecutiveService.sendWhatsAppLocationRequest(formData.phoneNumber)}
                  className="px-3 py-2 rounded-lg bg-emerald-500 text-white flex items-center gap-1"
                >
                  <MapPin size={16} />
                  Location
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Details *</label>
            <textarea
              value={formData.vehicleDetails}
              onChange={(event) => handleChange('vehicleDetails', event.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2"
              rows={3}
              placeholder="Vehicle number, model, color, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={formData.source}
              onChange={(event) => handleChange('source', event.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2"
            >
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isOtherSource && (
              <input
                value={formData.customSource}
                onChange={(event) => handleChange('customSource', event.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2 mt-2"
                placeholder="Specify other source"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location Link</label>
            <input
              value={formData.locationLink}
              onChange={(event) => handleChange('locationLink', event.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2"
              placeholder="Enter location link or coordinates"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Quoted Amount</label>
              <input
                value={formData.quotedAmount}
                onChange={(event) => handleChange('quotedAmount', event.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2"
                placeholder="Enter quoted amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advance Payment</label>
              <input
                value={formData.advancePayment}
                onChange={(event) => handleChange('advancePayment', event.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2"
                placeholder="Enter advance payment"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Media</label>
            <label className="flex flex-col items-center justify-center border border-dashed border-gray-300 rounded-lg p-4 text-gray-500 cursor-pointer">
              <Upload size={24} />
              <span className="text-sm mt-2">Click to upload images (max 5)</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(event) => handleChange('mediaFiles', Array.from(event.target.files || []).slice(0, 5))}
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={formData.problemDescription}
              onChange={(event) => handleChange('problemDescription', event.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2"
              rows={4}
              placeholder="Describe the problem in detail"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Number</label>
            <input
              value={formData.alternateNumber}
              onChange={(event) => handleChange('alternateNumber', cleanPhoneNumber(event.target.value))}
              className="w-full border border-gray-200 rounded-lg p-2"
              placeholder="Enter alternate number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
            <select
              value={formData.serviceType}
              onChange={(event) => handleChange('serviceType', event.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2"
            >
              {SERVICE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {formData.serviceType === 'towing' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Drop Location *</label>
              <textarea
                value={formData.dropLocation}
                onChange={(event) => handleChange('dropLocation', event.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2"
                rows={3}
                placeholder="Enter drop location for towing service"
              />
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-4 py-2 rounded-lg text-white ${submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {submitting ? 'Submitting...' : 'Send to Associated Advisor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceExecutiveComplaintRegistrationModal;
